import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Category, CheckCircle } from '@mui/icons-material'
import { Box, Card, CardActionArea, CardContent, Grid, Stack, Typography } from '@mui/material'

import GRImg from '../../../../../components/GRImg/GRImg'
import { LinearProgressBar } from '../../../../../components/LinearProgressBar/LinearProgressBar'
import { getAnswerCount, getCategoryProgress, getFutureFeatureCount, isCategoryCompleted } from '../../helpers/analysisOverviewHelpers'
import { ActiveFeatures, InternalAnalysis, OverviewCategory } from '../../types/InternalAnalysis'
import './AnalysisCategoryCards.scss'

type AnalysisCategoryCardsProps = {
  categories: OverviewCategory[]
  analysis: InternalAnalysis
  activeFeatures?: ActiveFeatures
  getCategoryPath: (categoryId: string) => string
}

export const AnalysisCategoryCards: FC<AnalysisCategoryCardsProps> = ({ categories, analysis, activeFeatures, getCategoryPath }) => {
  const { t } = useTranslation()

  // Grid's negative spacing margins are reset when it is a direct child of a Stack, so it needs its own wrapper.
  return (
    <Box>
      <Grid container wrap="wrap" spacing={2}>
        {categories.map((category) => {
          const completed = isCategoryCompleted(category, analysis)
          const progress = getCategoryProgress(category, analysis, activeFeatures)
          return (
            <Grid item xs={12} sm={6} md={4} key={category.id}>
              <Card variant="outlined" sx={{ height: '100%', borderColor: completed ? 'success.main' : undefined }}>
                <CardActionArea component={Link} to={getCategoryPath(category.id)} sx={{ height: '100%' }} aria-label={category.name}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                      <Box
                        className="AnalysisCategoryCards__icon"
                        sx={{ width: 48, height: 48, flexShrink: 0, '& img': { width: '100%', height: '100%', objectFit: 'contain' } }}
                      >
                        {category.icon?.url ? <GRImg alt="" src={category.icon.url} /> : <Category color="disabled" fontSize="large" />}
                      </Box>
                      <Box minWidth={0}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography fontWeight="bold">{category.name}</Typography>
                          {completed && <CheckCircle color="success" fontSize="small" />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {t('internal-analysts:analysis_overview_category_counts', {
                            answers: getAnswerCount(category, analysis),
                            future: getFutureFeatureCount(category, analysis, activeFeatures),
                            total: category.featureCount,
                          })}
                        </Typography>
                      </Box>
                    </Stack>
                    <LinearProgressBar value={progress} labelAfter={`${progress}%`} color={progress === 100 ? 'success' : 'primary'} />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}
